/**
 * @description: Fixed App.js with Clerk authentication and token sync
 * @author: USER
 * @modified: 2025 - Integrated ClerkProvider + Token Sync + Logs Enabled
 */
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { setClerkToken, clearClerkToken } from './services/api';

// Import Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ui/ScrollToTop';

// Import Pages
import Home from './pages/Home';
import About from './pages/about/About';
import Mission from './pages/about/Mission';
import Vision from './pages/about/Vision';
import Team from './pages/about/Team';
import Programs from './pages/Programs';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import { SignIn, SignUp } from '@clerk/clerk-react';
import CompleteProfile from './pages/auth/CompleteProfile';
import Events from './pages/Events';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RequireCompleteProfile from './components/auth/RequireCompleteProfile';
import AdminRoute from './components/AdminRoute';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ProgramDetails from './pages/programs/ProgramDetails';
import EventDetails from './pages/events/EventDetails';
import AcdAiWidget from './components/ai/AcdAiWidget';

// Import Styles
import './App.css';

// ✅ Get Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key');
}

// ✅ Token Sync Component - Keeps Clerk token synced with API layer
const ClerkTokenSync = () => {
  const { getToken, isSignedIn } = useAuth();

  useEffect(() => {
    const syncToken = async () => {
      if (isSignedIn) {
        try {
          const token = await getToken();
          if (token) {
            setClerkToken(token);
            //console.log('✅ Clerk token synced to API layer');

            // DEV: Decode and log token claims to help debug auth issues.
            // Only show in non-production to avoid leaking tokens in logs.
            if (process.env.NODE_ENV !== 'production') {
              try {
                const decodeBase64Url = (str) => {
                  // Replace URL-safe chars and pad base64
                  str = str.replace(/-/g, '+').replace(/_/g, '/');
                  while (str.length % 4) str += '=';
                  return JSON.parse(atob(str));
                };

                const parts = token.split('.');
                if (parts.length >= 2) {
                  const claims = decodeBase64Url(parts[1]);
                 // console.log('🧾 Clerk token claims (dev only):', claims);
                }
              } catch (err) {
               // console.warn('Could not decode Clerk token (dev):', err.message || err);
              }
            }
          }
        } catch (error) {
          //console.error('❌ Failed to sync Clerk token:', error);
        }
      } else {
        clearClerkToken();
        //console.log('🔒 User signed out, token cleared');
      }
    };

    syncToken();

    // Refresh token every 5 minutes to keep it fresh
    const interval = setInterval(syncToken, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isSignedIn, getToken]);

  return null;
};

function App() {
  // ✅ CONSOLE OVERRIDE DISABLED - Logs are now visible for debugging
  // Uncomment the code below if you want to disable console logs again
  
  
  useEffect(() => {
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
      trace: console.trace,
      table: console.table,
      group: console.group,
      groupEnd: console.groupEnd,
      groupCollapsed: console.groupCollapsed,
      time: console.time,
      timeEnd: console.timeEnd,
      count: console.count,
      assert: console.assert,
      clear: console.clear,
      dir: console.dir,
      dirxml: console.dirxml
    };

    console.log = () => {};
    console.warn = () => {};
    console.error = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.trace = () => {};
    console.table = () => {};
    console.group = () => {};
    console.groupEnd = () => {};
    console.groupCollapsed = () => {};
    console.time = () => {};
    console.timeEnd = () => {};
    console.count = () => {};
    console.assert = () => {};
    console.clear = () => {};
    console.dir = () => {};
    console.dirxml = () => {};

    if (typeof window !== 'undefined') {
      window.console = {
        log: () => {}, warn: () => {}, error: () => {}, info: () => {},
        debug: () => {}, trace: () => {}, table: () => {}, group: () => {},
        groupEnd: () => {}, groupCollapsed: () => {}, time: () => {},
        timeEnd: () => {}, count: () => {}, assert: () => {}, clear: () => {},
        dir: () => {}, dirxml: () => {}
      };
    }

    if (typeof window !== 'undefined' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = () => {};
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberUnmount = () => {};
    }

    return () => {
      // Cleanup if needed
    };
  }, []);
  

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      {/* ✅ Token sync component - MUST be inside ClerkProvider */}
      <ClerkTokenSync />
      
      <Router>
        <ScrollToTop />
        <div className="App min-h-screen bg-gray-50">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#1f2937',
                borderRadius: '12px',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />

          <Navbar />

          <main className="flex-1">
            <Routes>
              {/* Home Route */}
              <Route path="/" element={<Home />} />

              {/* About Section Routes */}
              <Route path="/about" element={<About />} />
              <Route path="/about/mission" element={<Mission />} />
              <Route path="/about/vision" element={<Vision />} />
              <Route path="/about/team" element={<Team />} />

              {/* Events Routes */}
              <Route path="/events/:eventId" element={<EventDetails />} />
              <Route path="/programs/:programId" element={<ProgramDetails />} />

              {/* Other Public Routes */}
              <Route path="/programs" element={<Programs />} />
              <Route path="/events" element={<Events />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />

              {/* Auth Routes - Using Clerk's Default Components */}
              <Route
                path="/register/*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
                    <SignUp
                      routing="path"
                      path="/register"
                      signInUrl="/login"
                      afterSignUpUrl="/complete-profile"
                      appearance={{
                        elements: {
                          rootBox: "mx-auto",
                          card: "shadow-xl rounded-3xl"
                        }
                      }}
                    />
                  </div>
                }
              />
              <Route
                path="/login/*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
                    <SignIn
                      routing="path"
                      path="/login"
                      signUpUrl="/register"
                      afterSignInUrl="/dashboard"
                      appearance={{
                        elements: {
                          rootBox: "mx-auto",
                          card: "shadow-xl rounded-3xl"
                        }
                      }}
                    />
                  </div>
                }
              />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Protected Routes - Require login AND completed profile */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <RequireCompleteProfile>
                    <Dashboard />
                  </RequireCompleteProfile>
                </ProtectedRoute>
              } />

              <Route path="/admin" element={
                <ProtectedRoute>
                  <RequireCompleteProfile>
                    <AdminRoute>
                      <SuperAdminDashboard />
                    </AdminRoute>
                  </RequireCompleteProfile>
                </ProtectedRoute>
              }/>
            </Routes>
          </main>

          <Footer />

          {/* ACD AI Floating Widget - Available on all pages */}
          <AcdAiWidget />
        </div>
      </Router>
    </ClerkProvider>
  );
}

export default App;
